import React, { useRef, useState } from 'react';
import { SERIES_COLORS, fmtNum, niceScale } from './chartTheme';

// Time-series area/line chart. Series share the x set of the FIRST series
// (buckets are aligned by the caller). Hover shows a crosshair + tooltip.
//
//   series: [{ name, color?, points: [{ x, y }] }]
//   formatX(x), formatY(y) — tick/tooltip formatters
export default function AreaChart({
  series = [],
  height = 200,
  formatX = fmtNum,
  formatY = fmtNum,
}) {
  const id = React.useId();
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null); // { i, leftPct }

  const W = 640;
  const H = height;
  const PAD = { l: 42, r: 12, t: 12, b: 22 };
  const iw = W - PAD.l - PAD.r;
  const ih = H - PAD.t - PAD.b;

  const base = series[0]?.points || [];
  if (!base.length) return <div className="ck-empty">No data</div>;

  const xs = base.map((p) => p.x);
  const xMin = xs[0];
  const xMax = xs[xs.length - 1] || xMin + 1;
  const yRaw = Math.max(...series.flatMap((s) => s.points.map((p) => p.y)), 1);
  const { max: yMax, ticks: yTicks } = niceScale(yRaw);

  const px = (x) => PAD.l + ((x - xMin) / (xMax - xMin || 1)) * iw;
  const py = (y) => PAD.t + (1 - y / yMax) * ih;

  const linePath = (pts) =>
    pts.map((p, i) => `${i ? 'L' : 'M'}${px(p.x).toFixed(1)},${py(p.y).toFixed(1)}`).join(' ');
  const areaPath = (pts) =>
    `${linePath(pts)} L${px(pts[pts.length - 1].x).toFixed(1)},${py(0)} L${px(pts[0].x).toFixed(1)},${py(0)} Z`;

  // ~5 x ticks across the domain.
  const xTickIdx = [];
  const step = Math.max(1, Math.round(base.length / 5));
  for (let i = 0; i < base.length; i += step) xTickIdx.push(i);
  if (xTickIdx[xTickIdx.length - 1] !== base.length - 1) xTickIdx.push(base.length - 1);

  const onMove = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * W;
    const t = (relX - PAD.l) / iw;
    const i = Math.max(0, Math.min(base.length - 1, Math.round(t * (base.length - 1))));
    setHover({ i, leftPct: (px(base[i].x) / W) * 100 });
  };

  return (
    <div className="ck-wrap" ref={wrapRef} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} className="ck-svg" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          {series.map((s, si) => (
            <linearGradient key={s.name} id={`a${id}${si}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color || SERIES_COLORS[si]} stopOpacity="0.22" />
              <stop offset="100%" stopColor={s.color || SERIES_COLORS[si]} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={PAD.l} x2={W - PAD.r} y1={py(v)} y2={py(v)} className="ck-grid" />
            <text x={PAD.l - 6} y={py(v) + 3} className="ck-tick" textAnchor="end">{formatY(v)}</text>
          </g>
        ))}
        {xTickIdx.map((i) => (
          <text key={i} x={px(base[i].x)} y={H - 6} className="ck-tick" textAnchor="middle">
            {formatX(base[i].x)}
          </text>
        ))}
        {series.map((s, si) => (
          <g key={s.name}>
            <path d={areaPath(s.points)} fill={`url(#a${id}${si})`} />
            <path d={linePath(s.points)} fill="none" stroke={s.color || SERIES_COLORS[si]} strokeWidth="1.8" className="ck-line" />
          </g>
        ))}
        {hover && (
          <g>
            <line x1={px(base[hover.i].x)} x2={px(base[hover.i].x)} y1={PAD.t} y2={H - PAD.b} className="ck-cross" />
            {series.map((s, si) => s.points[hover.i] && (
              <circle key={s.name} cx={px(s.points[hover.i].x)} cy={py(s.points[hover.i].y)} r="3.2" fill={s.color || SERIES_COLORS[si]} />
            ))}
          </g>
        )}
      </svg>
      {series.length > 1 && (
        <div className="ck-legend">
          {series.map((s, si) => (
            <span key={s.name}><i style={{ background: s.color || SERIES_COLORS[si] }} />{s.name}</span>
          ))}
        </div>
      )}
      {hover && (
        <div className="ck-tip" style={{ left: `${hover.leftPct}%` }}>
          <div className="ck-tip-title">{formatX(base[hover.i].x)}</div>
          {series.map((s, si) => s.points[hover.i] && (
            <div key={s.name} className="ck-tip-row">
              <i style={{ background: s.color || SERIES_COLORS[si] }} />
              {s.name}: <strong>{formatY(s.points[hover.i].y)}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
